import * as THREE from 'three';
import { validateCameraGraph } from './cameraGraph.js';
import { resolveCameraNavigation, validateCameraPose } from './cameraNavigation.js';
import { resolveCameraPose } from './cameraPose.js';
import { shareStructure } from './structuralSharing.js';
const DISCRIMINANT_KEYS = new Set([
    'shapeKind',
    'layoutKind',
    'anchorKind',
    'endpointKind',
    'anglePolicyKind',
    'parametricKind',
    'itemKind',
    'itemCollectionKind',
    'constellationKind',
    'stationKind',
    'framingKind',
]);
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function hasDiscriminant(value) {
    return Object.keys(value).some((key) => DISCRIMINANT_KEYS.has(key));
}
function readRotation(value) {
    if (!isRecord(value) || !isRecord(value.layout))
        return undefined;
    const rotation = value.layout.rotation;
    if (!Array.isArray(rotation) || rotation.length !== 3)
        return undefined;
    if (!rotation.every((component) => typeof component === 'number' && Number.isFinite(component)))
        return undefined;
    return [rotation[0], rotation[1], rotation[2]];
}
function readPosition(value) {
    if (!isRecord(value) || !isRecord(value.layout) || !isRecord(value.layout.anchor))
        return undefined;
    const position = value.layout.anchor.position;
    if (!Array.isArray(position) || position.length !== 3)
        return undefined;
    if (!position.every((component) => typeof component === 'number' && Number.isFinite(component)))
        return undefined;
    return [position[0], position[1], position[2]];
}
function deepMergePatch(base, patch) {
    if (patch === undefined)
        return base;
    if (Array.isArray(patch))
        return patch;
    if (typeof patch === 'function')
        return patch;
    if (!isRecord(patch))
        return patch;
    if (hasDiscriminant(patch))
        return patch;
    const baseRecord = isRecord(base) ? base : {};
    const out = { ...baseRecord };
    for (const [key, value] of Object.entries(patch)) {
        out[key] = deepMergePatch(out[key], value);
    }
    return out;
}
function replaceActivation(base, patch) {
    if (patch === undefined)
        return base;
    const merged = deepMergePatch(base ?? {}, patch);
    if (!('activation' in patch))
        return merged;
    return {
        ...merged,
        activation: patch.activation,
    };
}
export function applyInstantPatch(base, patch) {
    return normalizeFrameAgainstTopology(applyFramePatch(base, patch));
}
function applyFramePatch(base, patch) {
    const frame = {
        ...base,
    };
    if (patch.spaceKey !== undefined)
        frame.spaceKey = patch.spaceKey;
    if (patch.itemCollections !== undefined)
        frame.itemCollections = patch.itemCollections;
    if (patch.constellations !== undefined)
        frame.constellations = patch.constellations;
    if (patch.spaceRenderOptions !== undefined) {
        const spaceRenderOptions = replaceActivation(base.spaceRenderOptions, patch.spaceRenderOptions);
        if (spaceRenderOptions !== undefined)
            frame.spaceRenderOptions = spaceRenderOptions;
    }
    if (patch.itemParametricState !== undefined) {
        const mergedItemParametricState = deepMergePatch(base.itemParametricState ?? {}, patch.itemParametricState);
        const connectorItemByKey = { ...mergedItemParametricState.connectorItemByKey };
        let clearedConnectorTransient = false;
        for (const [itemKey, itemState] of Object.entries(patch.itemParametricState.connectorItemByKey ?? {})) {
            if (itemState?.progressiveActivation !== null && itemState?.endpointPositions !== null
                && itemState?.activationFrontPosition !== null)
                continue;
            const currentState = connectorItemByKey[itemKey];
            if (currentState === undefined)
                continue;
            const retainedState = { ...currentState };
            if (itemState?.progressiveActivation === null)
                delete retainedState.progressiveActivation;
            if (itemState?.endpointPositions === null)
                delete retainedState.endpointPositions;
            if (itemState?.activationFrontPosition === null)
                delete retainedState.activationFrontPosition;
            connectorItemByKey[itemKey] = retainedState;
            clearedConnectorTransient = true;
        }
        frame.itemParametricState = clearedConnectorTransient
            ? { ...mergedItemParametricState, connectorItemByKey }
            : mergedItemParametricState;
        for (const [itemKey, itemState] of Object.entries(patch.itemParametricState.entityItemByKey ?? {})) {
            if (itemState?.ringPulse !== null)
                continue;
            const entityItemByKey = { ...frame.itemParametricState.entityItemByKey };
            const key = itemKey;
            const retainedState = { ...entityItemByKey[key] };
            delete retainedState.ringPulse;
            entityItemByKey[key] = retainedState;
            frame.itemParametricState = { ...frame.itemParametricState, entityItemByKey };
        }
    }
    if (patch.entityItemLabelChannelsToRemoveByKey !== undefined) {
        const entityItemByKey = { ...frame.itemParametricState?.entityItemByKey };
        for (const [key, channels] of Object.entries(patch.entityItemLabelChannelsToRemoveByKey)) {
            if (!channels?.length)
                continue;
            const itemKey = key;
            const state = entityItemByKey[itemKey];
            if (!state?.label)
                continue;
            const label = { ...state.label };
            for (const channel of channels)
                delete label[channel];
            entityItemByKey[itemKey] = { ...state, label };
        }
        frame.itemParametricState = { ...frame.itemParametricState, entityItemByKey };
    }
    if (patch.itemBehaviorController !== undefined)
        frame.itemBehaviorController = patch.itemBehaviorController;
    if (patch.constellationParametricState !== undefined) {
        frame.constellationParametricState = deepMergePatch(base.constellationParametricState ?? {}, patch.constellationParametricState);
    }
    return frame;
}
/** Camera poses cannot change topology. Keep the exact returned frame for staging and publication. */
export function withFrameCameraPose(frame, pose) {
    return applyFramePatch(frame, { spaceRenderOptions: { camera: { pose } } });
}
export function derivePhaseFinalFrame(base, phase) {
    let result = isInstantTransitionPhase(phase)
        ? applyInstantPatch(base, phase.patch)
        : applyInterpolatedPatch(base, phase.patch, 1);
    if (phase.cameraNavigation !== undefined) {
        if (phase.patch.spaceRenderOptions?.camera?.pose !== undefined) {
            throw new Error('A phase cannot specify both cameraNavigation and a camera.pose patch.');
        }
        const cameraPose = resolveCameraNavigation(isInstantTransitionPhase(phase) ? result : base, phase.cameraNavigation);
        result = applyInstantPatch(result, { spaceRenderOptions: { camera: { pose: cameraPose } } });
    }
    validateCameraPose(resolveCameraPose(result.spaceRenderOptions));
    return result;
}
export function deriveTransitionSignature(base, phases) {
    if (phases.length === 0)
        return JSON.stringify({ base, phases });
    const keyedPhaseSignature = phases.map((phase, index) => {
        const transitionKey = phase.transitionOptions.transitionKey;
        if (transitionKey === undefined)
            return null;
        return [index, transitionKey, phase.transitionOptions.transitionKind].join(':');
    });
    if (keyedPhaseSignature.every((entry) => entry !== null)) {
        return `${base.spaceKey}|${keyedPhaseSignature.join('|')}`;
    }
    return JSON.stringify({ base, phases });
}
export function interpolateNumericLeaf(baseValue, targetValue, progress01) {
    const progress = Math.max(0, Math.min(1, progress01));
    if (typeof targetValue === 'number') {
        if (typeof baseValue !== 'number' || !Number.isFinite(baseValue) || !Number.isFinite(targetValue)) {
            throw new Error('Interpolated numeric patch requires finite numeric base and target values.');
        }
        return baseValue + ((targetValue - baseValue) * progress);
    }
    if (Array.isArray(targetValue)) {
        if (!Array.isArray(baseValue) || baseValue.length !== targetValue.length) {
            throw new Error('Interpolated array patch requires a same-length base array.');
        }
        return targetValue.map((targetElement, index) => interpolateNumericLeaf(baseValue[index], targetElement, progress));
    }
    if (isRecord(targetValue)) {
        if (!isRecord(baseValue)) {
            throw new Error('Interpolated object patch requires an existing base object.');
        }
        const out = {};
        for (const [key, value] of Object.entries(targetValue)) {
            out[key] = interpolateNumericLeaf(baseValue[key], value, progress);
        }
        return out;
    }
    throw new Error('Interpolated patch leaves must be numeric.');
}
export function interpolateNumericPatch(base, patch, progress01) {
    const out = {};
    if (patch.spaceRenderOptions?.camera?.pose !== undefined) {
        out.spaceRenderOptions = {
            camera: { pose: interpolateNumericLeaf(resolveCameraPose(base.spaceRenderOptions), patch.spaceRenderOptions.camera.pose, progress01) },
        };
    }
    if (patch.itemParametricState !== undefined) {
        const itemParametricState = interpolateNumericLeaf(base.itemParametricState, patch.itemParametricState, progress01);
        if (itemParametricState !== undefined)
            out.itemParametricState = itemParametricState;
    }
    for (const [itemKey, turn] of Object.entries(patch.entityItemRigidProfileRotationByKey ?? {})) {
        if (!turn || progress01 === 1)
            continue;
        const key = itemKey;
        const shape = base.itemParametricState?.entityItemByKey?.[key]?.shape;
        if (shape?.shapeKind !== 'extruded-profile')
            throw new Error('Rigid profile turns require an existing extruded profile.');
        const quaternion = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(...turn.axis).normalize(), turn.angle * Math.max(0, Math.min(1, progress01)));
        const rotate = (point) => new THREE.Vector3(...point).applyQuaternion(quaternion).toArray();
        const items = out.itemParametricState?.entityItemByKey;
        out.itemParametricState = {
            ...out.itemParametricState,
            entityItemByKey: {
                ...items,
                [key]: { ...items?.[key], shape: {
                        ...items?.[key]?.shape,
                        from: rotate(shape.from), to: rotate(shape.to), lateral: rotate(shape.lateral),
                    } },
            },
        };
    }
    if (patch.constellationParametricState !== undefined) {
        const constellationParametricState = interpolateNumericLeaf(base.constellationParametricState, patch.constellationParametricState, progress01);
        if (constellationParametricState !== undefined)
            out.constellationParametricState = constellationParametricState;
    }
    for (const [constellationKey, rotationDelta] of Object.entries(patch.entityConstellationLocalRotationDeltaByKey ?? {})) {
        if (rotationDelta === undefined)
            continue;
        const baseState = base.constellationParametricState?.entityConstellationByKey?.[constellationKey];
        const baseRotation = readRotation(baseState);
        if (baseRotation === undefined) {
            throw new Error('Local rotation interpolation requires an existing base layout rotation.');
        }
        const axis = new THREE.Vector3(...rotationDelta.axis);
        if (axis.lengthSq() <= 1e-12) {
            throw new Error('Local rotation interpolation requires a nonzero axis.');
        }
        const progress = Math.max(0, Math.min(1, progress01));
        const quaternion = new THREE.Quaternion()
            .setFromEuler(new THREE.Euler(...baseRotation, 'XYZ'))
            .multiply(new THREE.Quaternion().setFromAxisAngle(axis.normalize(), rotationDelta.angle * progress));
        const euler = new THREE.Euler().setFromQuaternion(quaternion, 'XYZ');
        const targetRotation = readRotation(patch.constellationParametricState?.entityConstellationByKey?.[constellationKey]);
        const interpolatedRotation = progress === 1
            ? targetRotation ?? [euler.x, euler.y, euler.z]
            : [euler.x, euler.y, euler.z];
        out.constellationParametricState = deepMergePatch(out.constellationParametricState ?? {}, {
            entityConstellationByKey: {
                [constellationKey]: { layout: { rotation: interpolatedRotation } },
            },
        });
    }
    for (const [constellationKey, orbit] of Object.entries(patch.entityConstellationOrbitByKey ?? {})) {
        if (orbit === undefined)
            continue;
        const baseState = base.constellationParametricState?.entityConstellationByKey?.[constellationKey];
        const basePosition = readPosition(baseState);
        const baseRotation = readRotation(baseState);
        if (basePosition === undefined || baseRotation === undefined) {
            throw new Error('Orbit interpolation requires an existing base position and rotation.');
        }
        const axis = new THREE.Vector3(...orbit.axis);
        if (axis.lengthSq() <= 1e-12) {
            throw new Error('Orbit interpolation requires a nonzero axis.');
        }
        const progress = Math.max(0, Math.min(1, progress01));
        const orbitQuaternion = new THREE.Quaternion().setFromAxisAngle(axis.normalize(), orbit.angle * progress);
        const position = new THREE.Vector3(...basePosition)
            .sub(new THREE.Vector3(...orbit.center))
            .applyQuaternion(orbitQuaternion)
            .add(new THREE.Vector3(...orbit.center));
        const rotationQuaternion = orbitQuaternion.clone().multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(...baseRotation, 'XYZ')));
        const rotationEuler = new THREE.Euler().setFromQuaternion(rotationQuaternion, 'XYZ');
        const targetState = patch.constellationParametricState?.entityConstellationByKey?.[constellationKey];
        const targetPosition = readPosition(targetState);
        const targetRotation = readRotation(targetState);
        const interpolatedPosition = progress === 1 && targetPosition !== undefined
            ? targetPosition
            : [position.x, position.y, position.z];
        const interpolatedRotation = progress === 1 && targetRotation !== undefined
            ? targetRotation
            : [rotationEuler.x, rotationEuler.y, rotationEuler.z];
        out.constellationParametricState = deepMergePatch(out.constellationParametricState ?? {}, {
            entityConstellationByKey: {
                [constellationKey]: {
                    layout: {
                        anchor: { position: interpolatedPosition },
                        rotation: interpolatedRotation,
                    },
                },
            },
        });
    }
    return out;
}
export function applyInterpolatedPatch(base, patch, progress01) {
    return applyInstantPatch(base, interpolateNumericPatch(base, patch, progress01));
}
function mergeInterpolatedPaths(base, patch, previous) {
    if (patch === undefined)
        return base;
    if (!isRecord(patch) || hasDiscriminant(patch)) {
        return shareStructure(previous, deepMergePatch(base, patch));
    }
    const baseRecord = isRecord(base) ? base : {};
    const previousRecord = isRecord(previous) ? previous : undefined;
    let out;
    let unchanged = previousRecord !== undefined;
    for (const [key, value] of Object.entries(patch)) {
        const merged = mergeInterpolatedPaths(baseRecord[key], value, previousRecord?.[key]);
        if (!Object.is(merged, baseRecord[key]) || !Object.prototype.hasOwnProperty.call(baseRecord, key)) {
            out ??= { ...baseRecord };
            out[key] = merged;
        }
        if (!previousRecord || !Object.is(merged, previousRecord[key]))
            unchanged = false;
    }
    return unchanged ? previous : out ?? baseRecord;
}
/**
 * Interpolated phases cannot change topology. Normalize once, then merge and compare
 * only numeric patch paths. Each sample is calculated from the fixed phase base.
 */
export function prepareInterpolatedPatch(base, patch) {
    // Validate before pruning so invalid references still produce the same errors.
    interpolateNumericPatch(base, patch, 0);
    const itemKeyInfo = collectItemKeyInfo(base.itemCollections);
    const constellationKeyInfo = collectConstellationKeyInfo(base.constellations);
    const normalized = normalizeFrameWithKeyInfo(base, itemKeyInfo, constellationKeyInfo);
    const { entityItemKeys, connectorItemKeys } = itemKeyInfo;
    const { entityConstellationKeys, connectorConstellationKeys } = constellationKeyInfo;
    return (progress01, previous = normalized) => {
        const numeric = interpolateNumericPatch(base, patch, progress01);
        if (numeric.itemParametricState) {
            const entityItemByKey = pruneRecordByKey(numeric.itemParametricState.entityItemByKey, entityItemKeys);
            const connectorItemByKey = pruneRecordByKey(numeric.itemParametricState.connectorItemByKey, connectorItemKeys);
            if (!entityItemByKey && !connectorItemByKey)
                delete numeric.itemParametricState;
            else
                numeric.itemParametricState = {
                    ...(entityItemByKey ? { entityItemByKey } : {}),
                    ...(connectorItemByKey ? { connectorItemByKey } : {}),
                };
        }
        if (numeric.constellationParametricState) {
            const entityConstellationByKey = pruneRecordByKey(numeric.constellationParametricState.entityConstellationByKey, entityConstellationKeys);
            const connectorConstellationByKey = pruneRecordByKey(numeric.constellationParametricState.connectorConstellationByKey, connectorConstellationKeys);
            if (!entityConstellationByKey && !connectorConstellationByKey)
                delete numeric.constellationParametricState;
            else
                numeric.constellationParametricState = {
                    ...(entityConstellationByKey ? { entityConstellationByKey } : {}),
                    ...(connectorConstellationByKey ? { connectorConstellationByKey } : {}),
                };
        }
        const next = { ...normalized };
        if (numeric.itemParametricState !== undefined) {
            next.itemParametricState = mergeInterpolatedPaths(normalized.itemParametricState, numeric.itemParametricState, previous.itemParametricState);
        }
        if (numeric.constellationParametricState !== undefined) {
            next.constellationParametricState = mergeInterpolatedPaths(normalized.constellationParametricState, numeric.constellationParametricState, previous.constellationParametricState);
        }
        if (numeric.spaceRenderOptions !== undefined) {
            next.spaceRenderOptions = mergeInterpolatedPaths(normalized.spaceRenderOptions, numeric.spaceRenderOptions, previous.spaceRenderOptions);
        }
        return next;
    };
}
function collectItemKeyInfo(itemCollections) {
    const entityItemKeys = new Set();
    const connectorItemKeys = new Set();
    const itemKeysByCollectionKey = new Map();
    for (const itemCollection of itemCollections) {
        const keys = new Set();
        for (const item of itemCollection.items) {
            keys.add(item.itemKey);
            if (item.itemKind === 'entity')
                entityItemKeys.add(item.itemKey);
            if (item.itemKind === 'connector')
                connectorItemKeys.add(item.itemKey);
        }
        itemKeysByCollectionKey.set(itemCollection.itemCollectionKey, keys);
    }
    return { entityItemKeys, connectorItemKeys, itemKeysByCollectionKey };
}
function collectConstellationKeyInfo(constellations) {
    const entityConstellationKeys = new Set();
    const connectorConstellationKeys = new Set();
    const collectionKeyByConstellationKey = new Map();
    for (const constellation of constellations) {
        collectionKeyByConstellationKey.set(constellation.constellationKey, constellation.itemCollectionKey);
        if (constellation.constellationKind === 'entity')
            entityConstellationKeys.add(constellation.constellationKey);
        if (constellation.constellationKind === 'connector')
            connectorConstellationKeys.add(constellation.constellationKey);
    }
    return { entityConstellationKeys, connectorConstellationKeys, collectionKeyByConstellationKey };
}
function pruneRecordByKey(record, keys) {
    if (record === undefined)
        return undefined;
    const recordKeys = Object.keys(record);
    if (recordKeys.length === 0)
        return undefined;
    if (recordKeys.every((key) => keys.has(key)))
        return record;
    const out = {};
    for (const key of recordKeys) {
        if (keys.has(key))
            out[key] = record[key];
    }
    return Object.keys(out).length === 0 ? undefined : out;
}
function normalizeActivation(args) {
    const { activation, entityConstellationKeys, itemKeysByCollectionKey, collectionKeyByConstellationKey } = args;
    if (activation === undefined)
        return undefined;
    const activeItemKeyByConstellationKey = (() => {
        const out = {};
        for (const [constellationKey, itemKey] of Object.entries(activation.activeItemKeyByConstellationKey ?? {})) {
            if (itemKey === undefined)
                continue;
            if (!entityConstellationKeys.has(constellationKey))
                continue;
            const collectionKey = collectionKeyByConstellationKey.get(constellationKey);
            const itemKeys = collectionKey === undefined ? undefined : itemKeysByCollectionKey.get(collectionKey);
            if (!itemKeys?.has(itemKey))
                continue;
            out[constellationKey] = itemKey;
        }
        return Object.keys(out).length === 0 ? undefined : out;
    })();
    const activationLinks = (activation.activationLinks ?? []).flatMap((activationLink) => {
        if (!entityConstellationKeys.has(activationLink.sourceConstellationKey))
            return [];
        if (!entityConstellationKeys.has(activationLink.targetConstellationKey))
            return [];
        const sourceCollectionKey = collectionKeyByConstellationKey.get(activationLink.sourceConstellationKey);
        const targetCollectionKey = collectionKeyByConstellationKey.get(activationLink.targetConstellationKey);
        const sourceItemKeys = sourceCollectionKey === undefined ? undefined : itemKeysByCollectionKey.get(sourceCollectionKey);
        const targetItemKeys = targetCollectionKey === undefined ? undefined : itemKeysByCollectionKey.get(targetCollectionKey);
        if (!sourceItemKeys || !targetItemKeys)
            return [];
        const targetItemKeysBySourceItemKey = {};
        for (const [sourceItemKey, targetKeys] of Object.entries(activationLink.targetItemKeysBySourceItemKey)) {
            if (targetKeys === undefined)
                continue;
            if (!sourceItemKeys.has(sourceItemKey))
                continue;
            const existingTargetKeys = targetKeys.filter((targetItemKey) => targetItemKeys.has(targetItemKey));
            if (existingTargetKeys.length > 0) {
                targetItemKeysBySourceItemKey[sourceItemKey] = existingTargetKeys;
            }
        }
        if (Object.keys(targetItemKeysBySourceItemKey).length === 0)
            return [];
        return [{
                sourceConstellationKey: activationLink.sourceConstellationKey,
                targetConstellationKey: activationLink.targetConstellationKey,
                targetItemKeysBySourceItemKey,
            }];
    });
    return shareStructure(activation, {
        ...(activeItemKeyByConstellationKey === undefined ? {} : { activeItemKeyByConstellationKey }),
        ...(activationLinks.length === 0 ? {} : { activationLinks }),
        ...(activation.nonActiveItemDimming === undefined ? {} : { nonActiveItemDimming: activation.nonActiveItemDimming }),
    });
}
export function normalizeFrameAgainstTopology(frame) {
    return normalizeFrameWithKeyInfo(frame, collectItemKeyInfo(frame.itemCollections), collectConstellationKeyInfo(frame.constellations));
}
function normalizeFrameWithKeyInfo(frame, itemKeyInfo, constellationKeyInfo) {
    const { entityItemKeys, connectorItemKeys, itemKeysByCollectionKey } = itemKeyInfo;
    const { entityConstellationKeys, connectorConstellationKeys, collectionKeyByConstellationKey } = constellationKeyInfo;
    const itemParametricState = (() => {
        const entityItemByKey = pruneRecordByKey(frame.itemParametricState?.entityItemByKey, entityItemKeys);
        const connectorItemByKey = pruneRecordByKey(frame.itemParametricState?.connectorItemByKey, connectorItemKeys);
        if (entityItemByKey === undefined && connectorItemByKey === undefined)
            return undefined;
        return shareStructure(frame.itemParametricState, {
            ...(entityItemByKey === undefined ? {} : { entityItemByKey }),
            ...(connectorItemByKey === undefined ? {} : { connectorItemByKey }),
        });
    })();
    const constellationParametricState = (() => {
        const entityConstellationByKey = pruneRecordByKey(frame.constellationParametricState?.entityConstellationByKey, entityConstellationKeys);
        const connectorConstellationByKey = pruneRecordByKey(frame.constellationParametricState?.connectorConstellationByKey, connectorConstellationKeys);
        if (entityConstellationByKey === undefined && connectorConstellationByKey === undefined)
            return undefined;
        return shareStructure(frame.constellationParametricState, {
            ...(entityConstellationByKey === undefined ? {} : { entityConstellationByKey }),
            ...(connectorConstellationByKey === undefined ? {} : { connectorConstellationByKey }),
        });
    })();
    const activation = normalizeActivation({
        activation: frame.spaceRenderOptions?.activation,
        entityConstellationKeys,
        itemKeysByCollectionKey,
        collectionKeyByConstellationKey,
    });
    const spaceRenderOptions = frame.spaceRenderOptions === undefined
        ? undefined
        : activation === frame.spaceRenderOptions.activation
            ? frame.spaceRenderOptions
            : {
                ...frame.spaceRenderOptions,
                ...(activation === undefined ? {} : { activation }),
            };
    const normalized = {
        ...frame,
    };
    if (spaceRenderOptions === undefined) {
        delete normalized.spaceRenderOptions;
    }
    else {
        normalized.spaceRenderOptions = spaceRenderOptions;
    }
    if (itemParametricState === undefined) {
        delete normalized.itemParametricState;
    }
    else {
        normalized.itemParametricState = itemParametricState;
    }
    if (constellationParametricState === undefined) {
        delete normalized.constellationParametricState;
    }
    else {
        normalized.constellationParametricState = constellationParametricState;
    }
    return normalized;
}
export function isInstantTransitionPhase(phase) {
    return phase.transitionOptions.transitionKind === 'instant';
}
export function deriveEffectiveFrameFromPhases(base, phases) {
    let effective = base;
    for (const phase of phases) {
        const duration = isInstantTransitionPhase(phase) ? phase.transitionOptions.delay ?? 0 : phase.transitionOptions.duration;
        if (!Number.isFinite(duration) || duration < 0) {
            throw new Error('Transition duration/delay must be finite and non-negative.');
        }
        if (!isInstantTransitionPhase(phase) && phase.transitionOptions.easing !== undefined
            && !['linear', 'smoothstep'].includes(phase.transitionOptions.easing)) {
            throw new Error('Unsupported transition easing.');
        }
        effective = derivePhaseFinalFrame(effective, phase);
    }
    return effective;
}
export function deriveEffectiveFrameFromOutput(output) {
    return deriveEffectiveFrameFromPhases(output.base, output.transitionPhases ?? []);
}
export function validateTransitionPlan(base, phases) {
    validateCameraPose(resolveCameraPose(base.spaceRenderOptions));
    if (base.spaceRenderOptions?.camera?.graph)
        validateCameraGraph(base.spaceRenderOptions.camera.graph);
    deriveEffectiveFrameFromPhases(base, phases);
}
